import Head from "next/head";
import Link from "next/link";

import { constants } from "src/shared/constants";
import { openPdfInNewTab } from "src/utils/utils";
import { global_config } from "src/shared/global_config";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text when the window to opt in is open. The opt-in window starts 48hrs. in advance of game day. After opting in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the 3 predetermined games in the subscription. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your Spurscription only guarantees you tickets to 3 specific games a month. You can review your monthly schedule on your homepage.",
  },
  {
    question: "Where are my tickets?",
    answer: (
      <>
        You will be notified when tickets are available 3 hours prior to game
        start and are delivered within your{" "}
        <Link href="https://am.ticketmaster.com/spurs/" target="_blank">
          Ticketmaster Account Manager
        </Link>{" "}
        and the Official Spurs App presented by Frost.
      </>
    ),
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        3 hours prior to game start you will receive a text confirming that
        tickets have been delivered to your{" "}
        <Link href="https://am.ticketmaster.com/spurs/" target="_blank">
          Ticketmaster Account Manager
        </Link>{" "}
        and the Official Spurs App presented by Frost.
      </>
    ),
  },
  {
    question: "Where will my seats be located?",
    answer: "Seats will be located in the Balcony Level around the arena.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Spurscription tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your Spurscription being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:support@sseticketshelp.freshdesk.com">
          box office support
        </Link>{" "}
        for any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer:
      "You can absolutely create a group to link with other pass holders! Navigate to the My Group tab in your menu and copy the invite. This will ensure you are seated next to or close enough to hand a drink to one another for the game. You will need to ensure your group is created and all members have joined at least 4 hours prior to game start in order to be seated with one another.",
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets & upgrades will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer:
      "An active purchase for the month is not refundable. To cancel your pass for the upcoming month opt-out through the Manage Subscription tab.",
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer:
      "To update your payment method, simply navigate to the Manage Subscription menu on the right side of your screen. You will see a section to update your payment method where you can update new information.",
  },
  {
    question: "How do I renew my subscription?",
    answer:
      "Your subscription will automatically renew on the 1st of the month! There is no action needed on your end to keep your subscription active.",
  },
  {
    question: "Can I pause my subscription?",
    answer:
      "Yes, once you navigate to the  Manage Subscription tab in your account you can elect to pause your subscription for that month during the opt out window from the 15th to the 26th of the month. You will receive a text message when this period is open.",
  },
  {
    question: "Can I cancel my subscription?",
    answer:
      "Simply navigate to the Manage Subscription tab to cancel your subscription. You can only do so during the monthly opt out period from the 15th to the 26th of the month.",
  },
];

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      Tickets are non-transferable and not eligible for resale. You also agree
      to receive SMS alerts regarding ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

let hasPixelLoaded = false;
export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();
  if (hasPixelLoaded) return null;
  hasPixelLoaded = true;

  return (
    <Head>
      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1300602487962870');
          fbq('track', 'PageView');
          window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
            const { cartRes } = e.detail;
            fbq('track', 'Purchase', {
              value: String(cartRes.totalAmount),
              currency: 'USD',
            });
            console.log('meta pixel purchase event fired.');
          });
          `,
        }}
      />

      <noscript
        id={`${TEAM_SCHEMA}-pixel-noscript`}
        dangerouslySetInnerHTML={{
          __html: `
            <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1300602487962870&ev=PageView&noscript=1" />
          `,
        }}
      />
    </Head>
  );
};

export const PRE_PURCHASE_FAQS: TTeamConfig["LANGUAGES"]["PRE_PURCHASE_FAQS"] =
  [
    {
      question: "What is a Spurscription?",
      answer: `A "Spurscription" is a monthly subscription offered by the San Antonio Spurs. As a subscriber, fans are guaranteed seats for three pre-selected games per month for $49 and exclusive access to all other home games.`,
    },
    {
      question: "How much does a Spurscription cost?",
      answer: "The Spurscription is $49/month!",
    },
    {
      question: "Can I cancel my Spurscription at any time?",
      answer:
        "The cancellation window opens on the 15th and closes on the 25th.",
    },
    {
      question: "Can I gift a Spurscription to someone else?",
      answer:
        "At this time, gifting is only available by calling the ticket office.",
    },
    {
      question: "How do I renew my Spurscription?",
      answer:
        "Spurscriptions renew automatically, but you'll receive a notification before the renewal date. You can also log into your account to manage renewal settings or make adjustments to your subscription before the renewal period.",
    },
    {
      question: "How do I access tickets?",
      answer:
        "You will receive a text when the window to check-in is open. The check-in window starts 48hrs. in advance of game day. After checking in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
    },
    {
      question: "Do I get a ticket to each game on the schedule?",
      answer:
        "Tickets are guaranteed for the 3 predetermined games in the subscription. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your Spurscription only guarantees you tickets to 3 specific games a month. You can review your monthly schedule on your homepage.",
    },
    {
      question: "Where are my tickets?",
      answer:
        "You will be notified when tickets are available 3 hours prior to game start and are delivered within your Ticketmaster Account Manager and the Official Spurs App presented by Frost.",
    },
    {
      question: "How are tickets sent to me?",
      answer:
        "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your Ticketmaster Account Manager and the Official Spurs App presented by Frost.",
    },
    {
      question: "Where will my seats be located?",
      answer: "Seats will be located in the Balcony Level around the arena.",
    },
    {
      question: "Can these tickets be transferred to a friend?",
      answer:
        "Spurscription tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your Spurscription being revoked.",
    },
    {
      question: "Can my seats be changed?",
      answer: (
        <>
          Once tickets have been sent to your account, they cannot be changed.
          Please contact{" "}
          <Link href="mailto:support@sseticketshelp.freshdesk.com">
            box office support
          </Link>{" "}
          for any assistance you may require.
        </>
      ),
    },
    {
      question: "Can I link tickets with other pass holders?",
      answer:
        "You can absolutely create a group to link with other pass holders! Navigate to the My Group tab in your menu and copy the invite. This will ensure you are seated next to or close enough to hand a drink to one another for the game. You will need to ensure your group is created and all members have joined at least 4 hours prior to game start in order to be seated with one another.",
    },
    {
      question: "Can I bring a friend to a game?",
      answer:
        "Guest tickets & upgrades will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
    },
    {
      question: "Can I receive a refund if I cannot attend a game?",
      answer:
        "We do not issue any refunds for games you are not able to attend.",
    },
    {
      question: "How can I get a refund and cancel my pass?",
      answer:
        "An active purchase for the month is not refundable. To cancel your pass for the upcoming month opt-out through the Manage Subscription tab.",
    },
    {
      question:
        "How can I change my payment method or update my expiration date?",
      answer:
        "To update your payment method, simply navigate to the Manage Subscription menu on the right side of your screen. You will see a section to update your payment method where you can update new information.",
    },
    {
      question: "How do I renew my subscription?",
      answer:
        "Your subscription will automatically renew on the 1st of the month! There is no action needed on your end to keep your subscription active.",
    },
    {
      question: "Can I pause my subscription?",
      answer:
        "Yes, once you navigate to the  Manage Subscription tab in your account you can elect to pause your subscription for that month during the opt out window from the 15th to the 26th of the month. You will receive a text message when this period is open.",
    },
    {
      question: "Can I cancel my subscription?",
      answer:
        "Simply navigate to the Manage Subscription tab to cancel your subscription. You can only do so during the monthly opt out period from the 15th to the 26th of the month.",
    },
  ];
